'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

// Sanitize string to remove invalid characters that cause DOMException in some browsers
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    // Remove null bytes, control characters, and other problematic Unicode characters
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').replace(/[\x80-\x9F]/g, '').replace(/\uFFFE|\uFFFF/g, '');
  } catch {
    return '';
  }
}
interface InstagramEmbedProps {
  embedJs: string;
}

// Skeleton component for Instagram loading state
const InstagramSkeleton = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-10 h-10 bg-gray-200 rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-3 mb-3">{t0}<div className="flex-1"><div className="h-4 bg-gray-200 rounded w-28 mb-1" /><div className="h-3 bg-gray-200 rounded w-20" /></div><div className="w-6 h-6 bg-gray-200 rounded" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="w-full bg-gray-200 rounded" style={{
      paddingTop: "100%"
    }} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex gap-4 mt-3 mb-2"><div className="h-6 w-6 bg-gray-200 rounded" /><div className="h-6 w-6 bg-gray-200 rounded" /><div className="h-6 w-6 bg-gray-200 rounded" /></div>;
    t4 = <div className="h-4 bg-gray-200 rounded w-24 mb-2" />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-white animate-pulse max-w-[540px] mx-auto">{t1}{t2}{t3}{t4}<div className="space-y-2"><div className="h-3 bg-gray-200 rounded w-full" /><div className="h-3 bg-gray-200 rounded w-3/4" /></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};

// Declare instgrm on window
declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: (element?: HTMLElement) => void;
      };
    };
  }
}
export default function InstagramEmbed({
  embedJs
}: InstagramEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!embedJs || !containerRef.current || hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    try {
      // Decode the content - handle both Base64 and plain HTML
      let decodedContent: string;
      try {
        // Try Base64 decoding first
        decodedContent = atob(embedJs);
      } catch {
        // If atob fails, the content might be plain HTML or URL-encoded
        try {
          decodedContent = decodeURIComponent(embedJs);
        } catch {
          // Use as-is if all decoding fails
          decodedContent = embedJs;
        }
      }

      // Sanitize the content
      const sanitizedContent = sanitizeForDOM(decodedContent);

      // Parse the content to extract HTML and scripts separately
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedContent;

      // Remove script tags from the content (we'll load Instagram script separately)
      const scripts = tempDiv.querySelectorAll('script');
      scripts.forEach(script => script.remove());

      // Set the HTML content (blockquote only, no scripts)
      containerRef.current.innerHTML = tempDiv.innerHTML;

      // Function to render the Instagram embed
      const renderInstagram = () => {
        if (window.instgrm && window.instgrm.Embeds && containerRef.current) {
          window.instgrm.Embeds.process(containerRef.current);

          // Check for iframe appearing (Instagram rendered)
          const checkForIframe = setInterval(() => {
            const iframe = containerRef.current?.querySelector('iframe');
            if (iframe) {
              clearInterval(checkForIframe);
              setIsLoading(false);
            }
          }, 100);

          // Timeout fallback
          setTimeout(() => {
            clearInterval(checkForIframe);
            setIsLoading(false);
          }, 10000);
        } else {
          setIsLoading(false);
        }
      };

      // Check if Instagram script is already loaded
      if (window.instgrm && window.instgrm.Embeds) {
        // Instagram is ready, render the embed
        renderInstagram();
      } else {
        // Check if script tag already exists
        const existingScript = document.querySelector('script[src*="instagram.com/embeds.js"], script[src*="instagram.com/en_US/embeds.js"]');
        if (existingScript) {
          // Script exists, wait for it to be ready
          const checkInstgrm = setInterval(() => {
            if (window.instgrm && window.instgrm.Embeds) {
              clearInterval(checkInstgrm);
              renderInstagram();
            }
          }, 100);

          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkInstgrm);
            if (window.instgrm && window.instgrm.Embeds) {
              renderInstagram();
            } else {
              setIsLoading(false);
            }
          }, 10000);
        } else {
          // Load Instagram script
          const script_0 = document.createElement('script');
          script_0.src = '//www.instagram.com/embed.js';
          script_0.async = true;
          script_0.onload = () => {
            // Wait for instgrm to be ready
            const checkInstgrm_0 = setInterval(() => {
              if (window.instgrm && window.instgrm.Embeds) {
                clearInterval(checkInstgrm_0);
                renderInstagram();
              }
            }, 100);

            // Timeout
            setTimeout(() => {
              clearInterval(checkInstgrm_0);
              renderInstagram();
            }, 5000);
          };
          script_0.onerror = () => {
            console.error('InstagramEmbed: Failed to load Instagram embed script');
            setError('Failed to load Instagram widget');
            setIsLoading(false);
          };
          document.head.appendChild(script_0);
        }
      }

      // Fallback timeout - hide skeleton after max wait time
      const fallbackTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 15000);
      return () => {
        clearTimeout(fallbackTimeout);
      };
    } catch (err) {
      console.error('InstagramEmbed: Error', err);
      setError('Failed to process Instagram embed');
      setIsLoading(false);
    }
  }, [embedJs]);
  if (error) {
    return <div className="mb-4 p-4 border border-red-300 bg-red-50 rounded">
        <p className="text-red-600">Error loading Instagram: {error}</p>
      </div>;
  }
  return <div className="mb-4">
      {isLoading && <InstagramSkeleton />}
      <div ref={containerRef} className={isLoading ? 'opacity-0 h-0 overflow-hidden' : ''} />
    </div>;
}